import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { ButtonSize, FormLabel, VerticalLayout } from 'widgets';
import { Button } from 'widgets/button';

export type FormButtonSelectOption = {
    value: unknown;
    disabled?: boolean;
};

export const FormButtonSelect = ({
    name,
    label,
    options,
    columns = 2,
    buttonSize = 'medium',
    renderFn,
}: {
    name: string;
    label: string;
    options: FormButtonSelectOption[];
    columns?: number;
    buttonSize?: ButtonSize;
    renderFn: (option: FormButtonSelectOption, selected: boolean) => ReactNode;
}) => {
    const { register, watch, setValue } = useFormContext();

    register(name);
    const value = watch(name);

    return (
        <VerticalLayout $gap={8}>
            <FormLabel>{label}</FormLabel>
            <OptionGrid $columns={columns}>
                {options.map((option, index) => {
                    const selected = option.value === value;
                    return (
                        <Button
                            key={index}
                            buttonType={selected ? 'primary' : 'secondary'}
                            size={buttonSize}
                            disabled={option.disabled}
                            onClick={() => {
                                if (option.value !== value) {
                                    setValue(name, option.value, {
                                        shouldValidate: true,
                                        shouldTouch: true,
                                    });
                                }
                            }}
                        >
                            {renderFn(option, selected)}
                        </Button>
                    );
                })}
            </OptionGrid>
        </VerticalLayout>
    );
};

const OptionGrid = styled.div<{ $columns: number }>`
    display: grid;
    grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
    gap: 8px 16px;
`;
